// Thin API client. Acquires an access token silently from MSAL and attaches it
// as a Bearer token to calls against the Functions API.
//
// Relative /api paths work in both environments:
//   - dev: Vite proxies /api -> http://localhost:7071 (func start)
//   - prod: Static Web Apps linked-backend routing serves /api from the Function App
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest, apiBase } from './authConfig';

/**
 * Acquire an access token for the API, falling back to interactive if silent fails.
 * @param {import('@azure/msal-browser').IPublicClientApplication} msalInstance
 * @param {import('@azure/msal-browser').AccountInfo} account
 */
async function getToken(msalInstance, account) {
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Silent failed (consent/expiry) — force interactive. Redirect returns to the app.
      await msalInstance.acquireTokenRedirect(apiRequest);
      return null; // redirect navigates away; nothing to return
    }
    throw err;
  }
}

/**
 * Call the API with an auth token attached.
 * @returns {Promise<{ status: number, ok: boolean, data: any }>}
 */
// Azure Functions on the Consumption plan can scale to zero. When a page opens
// several authenticated calls at once against a cold worker, the platform
// returns 503/502/504 until an instance warms up. Retry those transient
// gateway statuses with exponential backoff so the UI loads instead of erroring.
const RETRY_STATUSES = new Set([502, 503, 504]);
const MAX_RETRIES = 4;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function apiFetch(msalInstance, account, path, options = {}) {
  const token = await getToken(msalInstance, account);
  if (!token) return { status: 0, ok: false, data: null }; // redirecting

  let res;
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      res = await fetch(`${apiBase}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      // Network error (also common while a cold worker is spinning up). Retry.
      if (attempt < MAX_RETRIES) {
        await sleep(Math.min(400 * 2 ** attempt, 4000) + Math.random() * 200);
        attempt += 1;
        continue;
      }
      throw err;
    }

    if (RETRY_STATUSES.has(res.status) && attempt < MAX_RETRIES) {
      await sleep(Math.min(400 * 2 ** attempt, 4000) + Math.random() * 200);
      attempt += 1;
      continue;
    }
    break;
  }

  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  return { status: res.status, ok: res.ok, data };
}

// ---- Reservations API helpers -------------------------------------------

/** GET /api/spots — active, bookable inventory. */
export function fetchSpots(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/spots');
}

/**
 * GET /api/spots/{spotId}/reservations — confirmed bookings for availability.
 * @param {{from?:string, to?:string}} window optional date range (YYYY-MM-DD)
 */
export function fetchSpotReservations(msalInstance, account, spotId, window = {}) {
  const qs = new URLSearchParams();
  if (window.from) qs.set('from', window.from);
  if (window.to) qs.set('to', window.to);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch(msalInstance, account, `/api/spots/${spotId}/reservations${suffix}`);
}

/**
 * POST /api/reservations — book a spot for the signed-in member.
 * @param {{spotId:string, startDate:string, endDate:string}} booking
 */
export function createReservation(msalInstance, account, booking) {
  return apiFetch(msalInstance, account, '/api/reservations', {
    method: 'POST',
    body: JSON.stringify(booking),
  });
}

/** GET /api/me/reservations — the signed-in member's own bookings (with spot detail). */
export function fetchMyReservations(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/me/reservations');
}

/** GET /api/household/nights — household cabin-night usage for the caller. */
export function fetchHouseholdNightUsage(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/household/nights');
}

// ---- Invitations API helpers --------------------------------------------

/** GET /api/membership-types — active tiers (for the invite form). */
export function fetchMembershipTypes(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/membership-types');
}

/** GET /api/invitations — admin: list issued invites. */
export function fetchInvitations(msalInstance, account, status) {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch(msalInstance, account, `/api/invitations${suffix}`);
}

/**
 * POST /api/invitations — admin: issue a single-use invite.
 * @param {{email:string, membershipTypeId?:string, role?:'member'|'admin', expiresInDays?:number}} invite
 */
export function createInvitation(msalInstance, account, invite) {
  return apiFetch(msalInstance, account, '/api/invitations', {
    method: 'POST',
    body: JSON.stringify(invite),
  });
}

/**
 * POST /api/invitations/accept — invitee: redeem an invite token on first login.
 * Email is taken from the verified token server-side, never sent here.
 * @param {string} token
 */
export function acceptInvitation(msalInstance, account, token) {
  const active = account || msalInstance.getActiveAccount() || msalInstance.getAllAccounts()?.[0] || null;
  return apiFetch(msalInstance, active, '/api/invitations/accept', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// ---- Membership / billing API helpers -----------------------------------

/** GET /api/membership — the signed-in member's current membership summary. */
export function fetchMembership(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/membership');
}

/**
 * POST /api/checkout/membership — start a Stripe Checkout session for a tier.
 * Returns { url } to redirect the browser to Stripe-hosted Checkout.
 * @param {string} membershipTypeId
 */
export function startMembershipCheckout(msalInstance, account, membershipTypeId) {
  return apiFetch(msalInstance, account, '/api/checkout/membership', {
    method: 'POST',
    body: JSON.stringify({ membershipTypeId }),
  });
}

// ---- Community boards API helpers ---------------------------------------

/** GET /api/boards — active topic boards. */
export function fetchBoards(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/boards');
}

/**
 * GET /api/boards/{boardId}/threads — thread roots (paginated).
 * @param {{limit?:number, offset?:number}} page
 */
export function fetchThreads(msalInstance, account, boardId, page = {}) {
  const qs = new URLSearchParams();
  if (page.limit) qs.set('limit', page.limit);
  if (page.offset) qs.set('offset', page.offset);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch(msalInstance, account, `/api/boards/${boardId}/threads${suffix}`);
}

/** GET /api/threads/{threadId} — a thread root plus its replies. */
export function fetchThread(msalInstance, account, threadId) {
  return apiFetch(msalInstance, account, `/api/threads/${threadId}`);
}

/**
 * POST /api/boards/{boardId}/posts — create a thread root or a reply.
 * @param {{body:string, parentId?:string, isAnnouncement?:boolean}} post
 */
export function createPost(msalInstance, account, boardId, post) {
  return apiFetch(msalInstance, account, `/api/boards/${boardId}/posts`, {
    method: 'POST',
    body: JSON.stringify(post),
  });
}

/**
 * PATCH /api/posts/{postId} — admin: edit a post/reply body and (for roots) its
 * announcement flag. Pass isAnnouncement only when changing it.
 * @param {{body:string, isAnnouncement?:boolean}} patch
 */
export function updatePost(msalInstance, account, postId, patch) {
  return apiFetch(msalInstance, account, `/api/posts/${postId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** DELETE /api/posts/{postId} — admin: delete a post/reply (cascades replies for a root). */
export function deletePost(msalInstance, account, postId) {
  return apiFetch(msalInstance, account, `/api/posts/${postId}`, {
    method: 'DELETE',
  });
}

// ---- Broadcasts (admin bulk messaging) API helpers ----------------------

/** GET /api/broadcasts — admin: recent broadcast history. */
export function fetchBroadcasts(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/broadcasts');
}

/**
 * POST /api/broadcasts — admin: send a bulk message to opted-in members.
 * @param {{channel?:'email'|'sms', subject?:string, body:string}} broadcast
 */
export function sendBroadcast(msalInstance, account, broadcast) {
  const channel = broadcast.channel || 'email';
  const payload = channel === 'sms'
    ? { channel, body: broadcast.body }
    : { channel, subject: broadcast.subject, body: broadcast.body };
  return apiFetch(msalInstance, account, '/api/broadcasts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ---- Member profile / contact preferences API helpers -------------------

/** GET /api/me/profile — the signed-in member's editable contact preferences. */
export function fetchProfile(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/me/profile');
}

/**
 * PATCH /api/me/profile — update phone and email/SMS opt-in.
 * Only the provided fields are changed. Pass phone:'' to clear it.
 * @param {{phone?:string|null, smsOptIn?:boolean, emailOptIn?:boolean}} patch
 */
export function updateProfile(msalInstance, account, patch) {
  return apiFetch(msalInstance, account, '/api/me/profile', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ---- Membership applications (Issue #8) ---------------------------------

/**
 * POST /api/applications — PUBLIC. Submit a membership application from the
 * landing page with no auth token. Returns { status, ok, data }.
 * @param {{fullName:string, email:string, phone?:string, message?:string, consented?:boolean}} application
 */
export async function submitApplication(application) {
  const res = await fetch(`${apiBase}/api/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(application),
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  return { status: res.status, ok: res.ok, data };
}

/**
 * GET /api/applications — admin: list the intake queue, optionally by status.
 * @param {string} [status] new|contacted|approved|declined
 */
export function fetchApplications(msalInstance, account, status) {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch(msalInstance, account, `/api/applications${suffix}`);
}

/**
 * PATCH /api/applications/{id} — admin: update status and/or notes.
 * @param {{status:string, notes?:string}} patch
 */
export function updateApplication(msalInstance, account, id, patch) {
  return apiFetch(msalInstance, account, `/api/applications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * POST /api/applications/{id}/approve — admin: approve an application, which
 * creates a single-use invite and links it back to the application.
 * @param {{membershipTypeId?:string, role?:'member'|'admin', expiresInDays?:number}} [opts]
 */
export function approveApplication(msalInstance, account, id, opts = {}) {
  return apiFetch(msalInstance, account, `/api/applications/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

// ---- Household (family membership) API helpers --------------------------

/** GET /api/household — the caller's household (owner or family), or null. */
export function fetchHousehold(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/household');
}

/**
 * POST /api/household — create the caller's household (owner action).
 * @param {{name?:string}} [opts]
 */
export function createHousehold(msalInstance, account, opts = {}) {
  return apiFetch(msalInstance, account, '/api/household', {
    method: 'POST',
    body: JSON.stringify(opts),
  });
}

/**
 * POST /api/household/invites — owner: invite a family member to a shared seat.
 * @param {{email:string, expiresInDays?:number}} invite
 */
export function inviteHouseholdMember(msalInstance, account, invite) {
  return apiFetch(msalInstance, account, '/api/household/invites', {
    method: 'POST',
    body: JSON.stringify(invite),
  });
}

/** DELETE /api/household/members/{memberId} — owner: remove a family member. */
export function removeHouseholdMember(msalInstance, account, memberId) {
  return apiFetch(msalInstance, account, `/api/household/members/${memberId}`, {
    method: 'DELETE',
  });
}

// ---- Admin member management --------------------------------------------

/**
 * GET /api/manage/members — admin: list the roster, with optional filters.
 * @param {{search?:string, status?:string, role?:string, limit?:number, offset?:number}} [opts]
 */
export function fetchMembers(msalInstance, account, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.search) qs.set('search', opts.search);
  if (opts.status) qs.set('status', opts.status);
  if (opts.role) qs.set('role', opts.role);
  if (opts.limit) qs.set('limit', opts.limit);
  if (opts.offset) qs.set('offset', opts.offset);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch(msalInstance, account, `/api/manage/members${suffix}`);
}

/**
 * PATCH /api/manage/members/{id} — admin: change a member's role, status, or
 * display name. Only the provided fields are changed.
 * @param {{role?:'member'|'admin', status?:'active'|'suspended', displayName?:string}} patch
 */
export function updateMemberAdmin(msalInstance, account, id, patch) {
  return apiFetch(msalInstance, account, `/api/manage/members/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/**
 * PUT /api/manage/members/{id}/membership — admin: set a member's membership
 * category (manual override, no Stripe charge).
 * @param {string} membershipTypeId
 */
export function setMemberMembershipAdmin(msalInstance, account, id, membershipTypeId) {
  return apiFetch(msalInstance, account, `/api/manage/members/${id}/membership`, {
    method: 'PUT',
    body: JSON.stringify({ membershipTypeId }),
  });
}

/** PUT /api/manage/households/{id}/night-allowance — admin: set annual cabin nights. */
export function setHouseholdNightAllowanceAdmin(msalInstance, account, id, annualCabinNights) {
  return apiFetch(msalInstance, account, `/api/manage/households/${id}/night-allowance`, {
    method: 'PUT',
    body: JSON.stringify({ annualCabinNights }),
  });
}

/**
 * DELETE /api/manage/members/{id}/membership — admin: remove a member's
 * membership category (comp access with no paid tier). Refused by the API if
 * the member has an active Stripe-backed subscription.
 */
export function clearMemberMembershipAdmin(msalInstance, account, id) {
  return apiFetch(msalInstance, account, `/api/manage/members/${id}/membership`, {
    method: 'DELETE',
  });
}

// ---- Media library (gallery + slideshows) -------------------------------

/**
 * PUBLIC: list a media category's images. No auth — landing-page visitors call
 * this. Uses the shared apiBase so it works in dev (proxy) and prod (direct).
 * @param {'cabins'|'farm'|'experiences'|'future-vision'} category
 * @returns {Promise<{category:string, items:Array}>}
 */
export async function fetchMedia(category) {
  const res = await fetch(`${apiBase}/api/media/${category}`);
  if (!res.ok) throw new Error(`media ${category}: HTTP ${res.status}`);
  return res.json();
}

/**
 * ADMIN: upload an image to a category. Sends raw bytes with filename/caption
 * headers (matches the API's raw-body upload contract).
 * @param {File} file
 * @param {string} [caption]
 */
export async function uploadMedia(msalInstance, account, category, file, caption = '') {
  return apiFetch(msalInstance, account, `/api/media/${category}`, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-filename': encodeURIComponent(file.name || 'image.jpg'),
      'x-caption': encodeURIComponent(caption || ''),
    },
    body: file,
  });
}

/** ADMIN: update an image's caption and/or order. */
export function updateMedia(msalInstance, account, category, name, patch) {
  return apiFetch(msalInstance, account, `/api/media/${category}/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** ADMIN: delete an image. */
export function deleteMedia(msalInstance, account, category, name) {
  return apiFetch(msalInstance, account, `/api/media/${category}/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

// ---- Instagram featured posts -------------------------------------------

/** PUBLIC: featured Instagram posts (permalinks) to embed. Anonymous. */
export async function fetchFeaturedPosts() {
  const res = await fetch(`${apiBase}/api/instagram/featured`);
  if (!res.ok) throw new Error(`instagram featured: HTTP ${res.status}`);
  return res.json();
}

/** ADMIN: add a featured post by its Instagram URL. */
export function addFeaturedPost(msalInstance, account, url, caption = '') {
  return apiFetch(msalInstance, account, '/api/instagram/featured', {
    method: 'POST',
    body: JSON.stringify({ url, caption }),
  });
}

/** ADMIN: remove a featured post by shortcode. */
export function removeFeaturedPost(msalInstance, account, shortcode) {
  return apiFetch(msalInstance, account, `/api/instagram/featured/${encodeURIComponent(shortcode)}`, {
    method: 'DELETE',
  });
}

/** ADMIN: reorder featured posts (array of shortcodes in display order). */
export function reorderFeaturedPosts(msalInstance, account, shortcodes) {
  return apiFetch(msalInstance, account, '/api/instagram/featured/order', {
    method: 'PUT',
    body: JSON.stringify({ shortcodes }),
  });
}

/** ADMIN: set/replace the displayed photo for a featured post (raw image bytes). */
export function setFeaturedImage(msalInstance, account, shortcode, file) {
  return apiFetch(msalInstance, account, `/api/instagram/featured/${encodeURIComponent(shortcode)}/image`, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
}

// ---- Admin: spot blocks (maintenance / events) --------------------------

/** GET /api/manage/blocks — active blocks (optional spotId filter). */
export function fetchSpotBlocks(msalInstance, account, spotId) {
  const suffix = spotId ? `?spotId=${encodeURIComponent(spotId)}` : '';
  return apiFetch(msalInstance, account, `/api/manage/blocks${suffix}`);
}

/**
 * POST /api/manage/blocks — block a spot for a date range.
 * @param {{spotId:string, startDate:string, endDate:string, reason?:string, note?:string}} block
 */
export function createSpotBlock(msalInstance, account, block) {
  return apiFetch(msalInstance, account, '/api/manage/blocks', {
    method: 'POST',
    body: JSON.stringify(block),
  });
}

/** DELETE /api/manage/blocks/{id} — clear a block, re-opening those dates. */
export function cancelSpotBlock(msalInstance, account, id) {
  return apiFetch(msalInstance, account, `/api/manage/blocks/${id}`, { method: 'DELETE' });
}

// ---- Admin: all-member booking visibility (cleaning crew) ---------------

/**
 * GET /api/manage/reservations — bookings across all members (upcoming by default).
 * @param {{from?:string, to?:string, includePast?:boolean, spotId?:string, memberId?:string}} [opts]
 */
export function fetchAllReservations(msalInstance, account, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.from) qs.set('from', opts.from);
  if (opts.to) qs.set('to', opts.to);
  if (opts.includePast) qs.set('includePast', '1');
  if (opts.spotId) qs.set('spotId', opts.spotId);
  if (opts.memberId) qs.set('memberId', opts.memberId);
  const suffix = qs.toString() ? `?${qs}` : '';
  return apiFetch(msalInstance, account, `/api/manage/reservations${suffix}`);
}

// ---- Member service requests --------------------------------------------

/**
 * POST /api/me/requests — member submits a service request.
 * @param {{requestType:string, details?:string, requestedDate?:string, reservationId?:string}} req
 */
export function createMyRequest(msalInstance, account, req) {
  return apiFetch(msalInstance, account, '/api/me/requests', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** GET /api/me/requests — the signed-in member's own requests. */
export function fetchMyRequests(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/me/requests');
}

/** GET /api/manage/requests — admin queue (optional status filter). */
export function fetchAdminRequests(msalInstance, account, status) {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch(msalInstance, account, `/api/manage/requests${suffix}`);
}

/**
 * PATCH /api/manage/requests/{id} — admin update status/notes.
 * @param {{status?:string, adminNotes?:string}} patch
 */
export function updateAdminRequest(msalInstance, account, id, patch) {
  return apiFetch(msalInstance, account, `/api/manage/requests/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

// ---- Admin: direct member messaging (two-way) ---------------------------

/**
 * POST /api/manage/members/{id}/messages — email or text one member.
 * @param {{channel:'email'|'sms', subject?:string, body:string}} msg
 */
export function sendMemberMessage(msalInstance, account, memberId, msg) {
  return apiFetch(msalInstance, account, `/api/manage/members/${memberId}/messages`, {
    method: 'POST',
    body: JSON.stringify(msg),
  });
}

/** GET /api/manage/members/{id}/messages — full conversation (both directions). */
export function fetchMemberMessages(msalInstance, account, memberId) {
  return apiFetch(msalInstance, account, `/api/manage/members/${memberId}/messages`);
}

/** GET /api/manage/inbound-summary — per-member inbound counts for inbox badges. */
export function fetchInboundSummary(msalInstance, account) {
  return apiFetch(msalInstance, account, '/api/manage/inbound-summary');
}
